import { toast } from 'sonner';
import { useRef, useMemo, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import { Stack, Divider } from '@mui/material';
import Typography from '@mui/material/Typography';

import { usePermission } from 'src/hooks/use-permission';

import { useNotificationHubContext } from 'src/contexts/notification-context';
import { useNotificationOperations } from 'src/api/services/system_definition/systemService';

import { Scrollbar } from 'src/components/scrollbar';

import { NotificationItem } from './notification-item';
import { NotificationButton } from './notification-button';
import { NotificationDetailDialog } from './notification-detail-dialog';

// ----------------------------------------------------------------------

export function NotificationsContent() {
  const { canHomeModuleCreateNotification } = usePermission();

  // SignalR connection for notifications
  const {
    notifications: signalRNotifications,
    markAsRead,
    removeNotification,
  } = useNotificationHubContext();

  // Notification operations hook
  const { readNotification, deleteNotification } = useNotificationOperations();

  // Dialog state
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const listRef = useRef(null);

  // Sort notifications - unread first, then by date
  const notifications = useMemo(() => {
    if (!signalRNotifications || signalRNotifications.length === 0) return [];

    return [...signalRNotifications].sort((a, b) => {
      const aIsRead = a.isRead ?? false;
      const bIsRead = b.isRead ?? false;

      if (!aIsRead && bIsRead) return -1;
      if (aIsRead && !bIsRead) return 1;

      const aDate = new Date(a.createdAt);
      const bDate = new Date(b.createdAt);
      return bDate - aDate;
    });
  }, [signalRNotifications]);

  // Notification handlers
  const handleNotificationClick = useCallback((notification) => {
    // Open detail dialog
    setSelectedNotification(notification);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(async () => {
    setDialogOpen(false);

    if (selectedNotification && !selectedNotification.isRead) {
      try {
        console.log(selectedNotification);
        await readNotification(selectedNotification.id);
        markAsRead(selectedNotification.id);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    setSelectedNotification(null);
  }, [selectedNotification, readNotification, markAsRead]);

  const handleDeleteNotification = useCallback(
    async (notificationId) => {
      try {
        await deleteNotification(notificationId);
        removeNotification(notificationId);
        toast.success('Notification deleted');
      } catch (error) {
        console.error('Failed to delete notification:', error);
      }
    },
    [deleteNotification, removeNotification]
  );

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>
      {canHomeModuleCreateNotification() && <NotificationButton />}
    </Stack>
  );

  const renderNotificationsList = (
    <Scrollbar>
      <Box component="ul" ref={listRef}>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
              <NotificationItem
                notification={notification}
                onItemClick={handleNotificationClick}
                onDelete={handleDeleteNotification}
              />
            </Box>
          ))
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications found
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      {renderHead}
      <Divider sx={{ mt: 1 }} />
      {renderNotificationsList}

      {/* Notification Detail Dialog */}
      <NotificationDetailDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        notification={selectedNotification}
      />
    </>
  );
}
